/**
 * ImpersonationBanner — shown on every page while an admin is "logged in as" another user, so it is never
 * ambiguous whose account is on screen. One click returns to the admin's own session; it also returns
 * automatically when the time limit runs out.
 */

import React, { useEffect, useState } from 'react';
import { getImpersonation, stopImpersonation } from '@services/auth/impersonation';

const minutesLeft = (endsAt?: string) => (endsAt ? Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 60000)) : null);

export const ImpersonationBanner: React.FC = () => {
  const [state] = useState(getImpersonation);
  const [left, setLeft] = useState<number | null>(() => minutesLeft(state?.ends_at));

  useEffect(() => {
    if (!state) return;
    const tick = () => {
      const m = minutesLeft(state.ends_at);
      setLeft(m);
      if (m === 0) stopImpersonation(); // time's up -> back to the admin's own account
    };
    const id = window.setInterval(tick, 20_000);
    return () => window.clearInterval(id);
  }, [state]);

  if (!state) return null;

  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 py-2 text-sm font-medium bg-amber-500 text-black">
      <span>
        <span aria-hidden>👁️ </span>
        Viewing as <b>{state.target.name}</b> ({state.target.email})
        {state.admin_email ? <> — you are {state.admin_email}</> : null}
        {left != null ? <> · ends in {left} min</> : null}
      </span>
      <button
        type="button"
        onClick={stopImpersonation}
        className="px-3 py-1 rounded-md bg-black text-white text-xs font-bold hover:bg-black/80"
      >
        ← Return to my account
      </button>
    </div>
  );
};

export default ImpersonationBanner;
